import type { Right } from '@gozoki/tools'
import type { AppAbility, PermissionTuple } from '~/utils/ability'
import {
  resolveMissingPermissions,
  type PermissionRequirement,
} from '~/utils/permissionRequirement'

/**
 * Décision d'autorisation d'un onglet, hors React : la règle de `useAuthorizedTabs`
 * (`utils/rbacTabs.tsx`), isolée pour être testable et partagée avec la palette de navigation
 * (⌘K), qui ne propose que les onglets que leur barre ouvre.
 */
/** Garde d'un onglet, étendue par `GuardedTab` (`utils/rbacTabs.tsx`). */
export interface TabGuard {
  /** Override explicite quand l'onglet n'a pas de route dédiée. */
  permission?: PermissionRequirement
  /**
   * Droits v1 exigés tant que le flag `permission-v2` est OFF (où les tuples v2 passent tous).
   * À renseigner sur tout onglet dont la visibilité était gardée par `useRights(...)`.
   */
  legacyRights?: Right[]
}

export interface TabAuthorizationContext {
  ability: AppAbility
  /** Le flag `permission-v2` de l'admin courant (`useRbacV2Enabled`). */
  v2Enabled: boolean
  /** `hasRights` de l'`AuthContext` : tous les bits v1 donnés. */
  hasRights: (rights: Right[]) => boolean
}

export interface TabAuthorization {
  authorized: boolean
  missingPermissions: PermissionTuple[]
}

/**
 * Flag ON : l'onglet est ouvert si sa permission v2 est déclarée et satisfaite — jamais par un bit
 * legacy. Flag OFF : les tuples v2 passent tous, donc seuls les `legacyRights` discriminent encore.
 */
export const resolveTabAuthorization = (
  { permission, legacyRights }: TabGuard,
  { ability, v2Enabled, hasRights }: TabAuthorizationContext,
): TabAuthorization => {
  const missingPermissions = resolveMissingPermissions(ability, permission)
  const hasLegacyRequirement = !!legacyRights && legacyRights.length > 0
  const legacyAuthorized = hasLegacyRequirement
    ? hasRights(legacyRights ?? [])
    : missingPermissions.length === 0
  const authorized = v2Enabled
    ? permission !== undefined && missingPermissions.length === 0
    : legacyAuthorized

  return { authorized, missingPermissions }
}
